/**
 * @fileoverview File Upload Module - Configurable file upload component
 * 
 * This module provides a reusable file upload interface that can be configured
 * for different contexts (user knowledge base, parrot configuration, admin).
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6, 1.7
 */

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useModuleContext, useModuleEvents } from '../context/ModuleContextProvider.jsx';
import { ModuleEventType } from '../types/index.js';
import { FileUploadConfig, validateFileUploadConfig } from './types.js';
import { FileUploadInterface } from './components/FileUploadInterface.jsx';
import { FileUploadProgress } from './components/FileUploadProgress.jsx';
import { FileUploadErrorDisplay } from './components/FileUploadErrorDisplay.jsx';
import { useFileUploadSecurity } from './hooks/useFileUploadSecurity.js';
import { useFileUploadValidation } from './hooks/useFileUploadValidation.js';
import { useFileUploadProgress } from './hooks/useFileUploadProgress.js';
import { fileUploadService } from './services/FileUploadService.js';

/**
 * File Upload Module Component
 * 
 * @param {Object} props - Component props
 * @param {FileUploadConfig} props.config - Module configuration
 * @param {ModuleContext} props.context - Module context
 * @param {function(ModuleEvent): void} [props.onEvent] - Event handler
 * @param {string} [props.className] - CSS class name
 * @param {string} [props.testId] - Test ID
 */
const FileUploadModule = ({ 
  config, 
  context, 
  onEvent, 
  className = '', 
  testId = 'file-upload-module' 
}) => {
  // Validate configuration
  const configValidation = validateFileUploadConfig(config);
  if (!configValidation.valid) {
    throw new Error(`Invalid FileUpload configuration: ${configValidation.errors.join(', ')}`);
  }

  // Module context and events
  const moduleContext = useModuleContext();
  const toast = useMemo(() => {
    return (
      context?.toast ||
      moduleContext?.toast ||
      { success: () => {}, error: () => {}, warning: () => {}, info: () => {} }
    );
  }, [context, moduleContext]);
  const { emit: emitEvent } = useModuleEvents('*', null, { enabled: false });

  // Module state
  const [moduleState, setModuleState] = useState({
    initialized: false,
    uploading: false,
    error: null
  });

  // Custom hooks for functionality
  const security = useFileUploadSecurity(config, context);
  const validation = useFileUploadValidation(config);
  const progress = useFileUploadProgress();

  // File input reference
  const fileInputRef = useRef(null);

  // Initialize module
  useEffect(() => {
    try {
      // Validate security context
      security.validateContext();
      
      // Ensure upload service uses an authenticated ApiClient
      try {
        const client = context?.apiClient || moduleContext?.apiClient;
        if (client) {
          fileUploadService.apiClient = client;
        }
      } catch (_) {
        // Non-fatal; will fallback to unauthenticated client if not available
      }

      setModuleState(prev => ({ ...prev, initialized: true }));
      
      // Emit module loaded event
      emitEvent({
        type: ModuleEventType.MODULE_LOADED,
        payload: {
          moduleId: 'file-upload-module',
          context: config.context,
          securityLevel: config.securityLevel
        },
        source: 'file-upload-module',
        timestamp: Date.now()
      });

    } catch (error) {
      console.error('FileUploadModule initialization error:', error);
      setModuleState(prev => ({ 
        ...prev, 
        initialized: false, 
        error: error.message 
      }));
    }
  }, [config, security, emitEvent]);

  /**
   * Handle file selection from input or drag-drop
   */
  const handleFileSelection = useCallback(async (files) => {
    if (!files || files.length === 0) return;

    try {
      setModuleState(prev => ({ ...prev, uploading: true, error: null }));

      // Ensure upload service has api client before starting upload
      if (!fileUploadService.apiClient && (context?.apiClient || moduleContext?.apiClient)) {
        fileUploadService.apiClient = context?.apiClient || moduleContext?.apiClient;
      }

      // Emit upload start event
      emitEvent({
        type: ModuleEventType.USER_ACTION,
        payload: {
          action: 'file_upload_started',
          fileCount: files.length,
          context: config.context
        },
        source: 'file-upload-module',
        timestamp: Date.now()
      });

      // Validate files
      const validationResult = validation.validateFiles(files);
      if (validationResult.invalidFiles.length > 0) {
        // Show validation errors but continue with valid files
        validationResult.invalidFiles.forEach(({ file, errors }) => {
          toast.error(`${file.name}: ${errors.join(', ')}`);
        });
      }

      if (validationResult.validFiles.length === 0) {
        setModuleState(prev => ({ ...prev, uploading: false }));
        return;
      }

      // Security validation
      const securityResult = await security.validateFiles(validationResult.validFiles);
      if (!securityResult.valid) {
        toast.error(`Security validation failed: ${securityResult.errors.join(', ')}`);
        setModuleState(prev => ({ ...prev, uploading: false }));
        return;
      }

      // Start upload process
      await processFileUploads(validationResult.validFiles);

    } catch (error) {
      console.error('File selection error:', error);
      toast.error(`Upload failed: ${error.message}`);
      setModuleState(prev => ({ ...prev, uploading: false, error: error.message }));
    }
  }, [config, context, validation, security, emitEvent]);

  /**
   * Process file uploads with enhanced batch processing and progress tracking
   */
  const processFileUploads = useCallback(async (files) => {
    const uploadResults = [];
    const uploadErrors = [];

    try {
      // Initialize progress tracking
      progress.initializeUploads(files);

      // Process uploads based on configuration
      if (config.concurrent && files.length > 1) {
        // Concurrent uploads with controlled concurrency
        const maxConcurrent = config.maxConcurrentUploads || 3;
        const batches = [];
        
        // Split files into batches
        for (let i = 0; i < files.length; i += maxConcurrent) {
          batches.push(files.slice(i, i + maxConcurrent));
        }

        // Process each batch concurrently
        for (const batch of batches) {
          const batchPromises = batch.map(async (file, batchIndex) => {
            const globalIndex = files.indexOf(file);
            try {
              const result = await uploadSingleFile(file, globalIndex);
              uploadResults.push(result);
              return result;
            } catch (error) {
              uploadErrors.push({ file, error, index: globalIndex });
              throw error;
            }
          });

          // Wait for current batch to complete before starting next
          const batchResults = await Promise.allSettled(batchPromises);
          
          // Add small delay between batches to prevent overwhelming the server
          if (batches.indexOf(batch) < batches.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      } else {
        // Sequential uploads with progress updates
        for (let i = 0; i < files.length; i++) {
          try {
            // Update overall progress
            emitEvent({
              type: ModuleEventType.MODULE_DATA_CHANGED,
              payload: {
                action: 'batch_progress_update',
                currentFile: i + 1,
                totalFiles: files.length,
                fileName: files[i].name
              },
              source: 'file-upload-module',
              timestamp: Date.now()
            });

            const result = await uploadSingleFile(files[i], i);
            uploadResults.push(result);

            // Add small delay between sequential uploads
            if (i < files.length - 1) {
              await new Promise(resolve => setTimeout(resolve, 50));
            }
          } catch (error) {
            uploadErrors.push({ file: files[i], error, index: i });
          }
        }
      }

      // Handle upload completion
      await handleUploadCompletion(uploadResults, uploadErrors);

    } catch (error) {
      console.error('Upload processing error:', error);
      throw error;
    } finally {
      setModuleState(prev => ({ ...prev, uploading: false }));
    }
  }, [config, progress, emitEvent]);

  /**
   * Upload a single file with progress tracking and R2 storage integration
   */
  const uploadSingleFile = useCallback(async (file, index) => {
    try {
      // Update progress to uploading
      progress.updateFileProgress(index, { status: 'uploading', progress: 0 });

      // Prepare upload configuration
      const uploadConfig = {
        context: config.context,
        securityLevel: config.securityLevel,
        triggerSync: config.triggerSync,
        parrotId: config.parrotId,
        metadata: config.metadata
      };

      // Upload with progress tracking using the service
      const response = await fileUploadService.uploadFile(
        file,
        uploadConfig,
        (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          progress.updateFileProgress(index, { 
            progress: percentCompleted,
            loaded: progressEvent.loaded,
            total: progressEvent.total
          });
        }
      );

      // Update progress to completed
      progress.updateFileProgress(index, { 
        status: 'completed', 
        progress: 100,
        result: response
      });

      // Emit file uploaded event
      emitEvent({
        type: ModuleEventType.MODULE_DATA_CHANGED,
        payload: {
          action: 'file_uploaded',
          file: {
            name: file.name,
            size: file.size,
            type: file.type,
            id: response.file?.id,
            r2_key: response.file?.r2_key,
            storage_name: response.file?.storage_name
          },
          context: config.context,
          sync: response.sync
        },
        source: 'file-upload-module',
        timestamp: Date.now()
      });

      return response;

    } catch (error) {
      // Update progress to failed
      progress.updateFileProgress(index, { 
        status: 'failed', 
        error: error.message 
      });

      throw error;
    }
  }, [config, progress, emitEvent]);

  /**
   * Handle upload completion
   */
  const handleUploadCompletion = useCallback(async (results, errors) => {
    try {
      // Invalidate cached KB lists if any successful uploads BEFORE external callbacks
      if (results.length > 0) {
        try {
          moduleContext?.apiClient?.invalidateCaches?.([
            '/api/knowledge-base',
            '/api/knowledge-base/files',
            '/api/knowledge-base/usage-stats'
          ]);
        } catch (_) {}
      }

      // Call completion callback if provided (e.g., to refetch lists)
      if (config.onUploadComplete) {
        await config.onUploadComplete({
          successful: results,
          failed: errors,
          total: results.length + errors.length
        });
      }

      // Show completion notifications
      if (results.length > 0) {
        toast.success(
          `Successfully uploaded ${results.length} file${results.length !== 1 ? 's' : ''}`
        );
      }

      if (errors.length > 0) {
        toast.error(
          `Failed to upload ${errors.length} file${errors.length !== 1 ? 's' : ''}`
        );
      }

      // Emit completion event
      emitEvent({
        type: ModuleEventType.USER_ACTION,
        payload: {
          action: 'file_upload_completed',
          successful: results.length,
          failed: errors.length,
          context: config.context
        },
        source: 'file-upload-module',
        timestamp: Date.now()
      });

      // Trigger sync if configured
      if (config.triggerSync && results.length > 0) {
        await triggerSyncServices(results);
      }

    } catch (error) {
      console.error('Upload completion error:', error);
    }
  }, [config, context, emitEvent]);

  /**
   * Trigger external sync services (e.g., ElevenLabs)
   */
  const triggerSyncServices = useCallback(async (uploadResults) => {
    try {
      if (config.triggerSync && config.parrotId && uploadResults.length > 0) {
        // Extract files that were successfully uploaded
        const successfulFiles = uploadResults
          .filter(result => result.file)
          .map(result => result.file);

        if (successfulFiles.length > 0) {
          const syncResult = await fileUploadService.triggerSync(
            successfulFiles,
            config.parrotId,
            config.context
          );

          emitEvent({
            type: ModuleEventType.SYSTEM_EVENT,
            payload: {
              event: 'sync_triggered',
              fileCount: successfulFiles.length,
              context: config.context,
              syncResult
            },
            source: 'file-upload-module',
            timestamp: Date.now()
          });

          if (syncResult.success) {
            toast.success(
              `Files synced successfully with ${syncResult.successful_syncs} of ${syncResult.total_files} files`
            );
          } else {
            toast.warning(
              `Partial sync completed: ${syncResult.successful_syncs} of ${syncResult.total_files} files synced`
            );
          }
        }
      }
    } catch (error) {
      console.error('Sync trigger error:', error);
      toast.warning('Files uploaded but sync failed. Please try syncing manually.');
    }
  }, [config, context, emitEvent]);

  /**
   * Handle file input change
   */
  const handleFileInputChange = useCallback((event) => {
    const files = Array.from(event.target.files);
    handleFileSelection(files);
    // Reset input to allow selecting the same files again
    event.target.value = '';
  }, [handleFileSelection]);

  /**
   * Handle drag and drop
   */
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const files = Array.from(event.dataTransfer.files);
    handleFileSelection(files);
  }, [handleFileSelection]);

  /**
   * Open file dialog
   */
  const openFileDialog = useCallback(() => {
    if (fileInputRef.current && !moduleState.uploading) {
      fileInputRef.current.click();
    }
  }, [moduleState.uploading]);

  // Show error state if module failed to initialize
  if (!moduleState.initialized && moduleState.error) {
    return (
      <div className={`file-upload-module-error ${className}`} data-testid={`${testId}-error`}>
        <div className="error-content">
          <h3>File Upload Module Error</h3>
          <p>{moduleState.error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="retry-button"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // Show loading state while initializing
  if (!moduleState.initialized) {
    return (
      <div className={`file-upload-module-loading ${className}`} data-testid={`${testId}-loading`}>
        <div className="loading-content">
          <div className="loading-spinner" />
          <p>Initializing file upload...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`file-upload-module ${className}`} data-testid={testId}>
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        multiple={config.multiple}
        accept={config.allowedTypes.map(type => `.${type}`).join(',')}
        onChange={handleFileInputChange}
        disabled={moduleState.uploading}
        className="hidden"
        data-testid={`${testId}-input`}
      />

      {/* Upload Interface */}
      <FileUploadInterface
        config={config}
        uploading={moduleState.uploading}
        onDrop={handleDrop}
        onOpenDialog={openFileDialog}
        testId={`${testId}-interface`}
      />

      {/* Progress Display */}
      {config.showProgress && progress.uploads.length > 0 && (
        <FileUploadProgress
          uploads={progress.uploads}
          testId={`${testId}-progress`}
        />
      )}

      {/* Error Display */}
      {progress.errors.length > 0 && (
        <FileUploadErrorDisplay
          errors={progress.errors}
          onRetry={progress.retryFailedUploads}
          onDismiss={progress.dismissError}
          testId={`${testId}-errors`}
        />
      )}
    </div>
  );
};

/**
 * Get default configuration for FileUploadModule
 * Requirements: 1.1, 1.2, 1.3, 1.4, 1.5, 1.6
 */
FileUploadModule.getDefaultConfig = () => ({
  context: 'user-kb',
  securityLevel: 'user',
  allowedTypes: ['pdf', 'docx', 'txt', 'html'], // Strict validation: NO JSON/CSV per requirements 1.2, 1.3
  maxFileSize: 4 * 1024 * 1024, // 4MB per requirements 1.2, 1.5
  multiple: true, // Multiple file selection per requirements 1.1
  showProgress: true, // Progress tracking per requirements 1.4
  concurrent: false,
  uploadEndpoint: '/api/modules/file-upload',
  triggerSync: false,
  syncEndpoint: '/api/modules/file-upload/sync'
});

export default FileUploadModule;
